import type { PlayerRole } from "../game/types";

export type FormationId = "4-3-3" | "4-4-2" | "4-2-3-1";
export type TacticalPresetId = "measured" | "vertical" | "control" | "shield";

export interface SquadPlayer {
  id: string;
  name: string;
  shortName: string;
  number: number;
  position: PlayerRole;
  alternatePositions: PlayerRole[];
  preferredFoot: "Left" | "Right";
  rating: number;
  heightCm: number;
  ability: string;
  abilityDetail: string;
  attributes: {
    pace: number;
    shooting: number;
    passing: number;
    dribbling: number;
    defending: number;
    physical: number;
  };
}

export interface FormationSlot {
  id: string;
  role: PlayerRole;
  label: string;
  x: number;
  y: number;
  anchorX: number;
  anchorZ: number;
}

export interface FormationDefinition {
  id: FormationId;
  name: string;
  summary: string;
  slots: FormationSlot[];
}

export interface TacticalPreset {
  id: TacticalPresetId;
  name: string;
  summary: string;
  buildUp: number;
  width: number;
  pressure: number;
}

export interface ClubPlan {
  schemaVersion: 1;
  formationId: FormationId;
  tacticalPreset: TacticalPresetId;
  startingIds: string[];
  captainId: string;
  selectedPlayerId: string;
}

export const SQUAD_PLAYERS: SquadPlayer[] = [
  {
    id: "ari-pradana",
    name: "Ari Pradana",
    shortName: "Ari",
    number: 1,
    position: "GK",
    alternatePositions: [],
    preferredFoot: "Right",
    rating: 78,
    heightCm: 188,
    ability: "Calm Reach",
    abilityDetail: "Quicker set stance before close-range saves.",
    attributes: { pace: 48, shooting: 18, passing: 70, dribbling: 52, defending: 76, physical: 79 }
  },
  {
    id: "sena-wardana",
    name: "Sena Wardana",
    shortName: "Sena",
    number: 2,
    position: "RB",
    alternatePositions: ["LB"],
    preferredFoot: "Right",
    rating: 76,
    heightCm: 177,
    ability: "Touchline Drive",
    abilityDetail: "Keeps better control while accelerating into wide space.",
    attributes: { pace: 81, shooting: 55, passing: 72, dribbling: 75, defending: 74, physical: 70 }
  },
  {
    id: "raka-wiratama",
    name: "Raka Wiratama",
    shortName: "Raka",
    number: 3,
    position: "LB",
    alternatePositions: ["RB"],
    preferredFoot: "Left",
    rating: 77,
    heightCm: 179,
    ability: "Measured Overlap",
    abilityDetail: "Times supporting runs without abandoning the defensive line.",
    attributes: { pace: 80, shooting: 57, passing: 74, dribbling: 73, defending: 76, physical: 72 }
  },
  {
    id: "bima-angkasa",
    name: "Bima Angkasa",
    shortName: "Bima",
    number: 4,
    position: "CB",
    alternatePositions: [],
    preferredFoot: "Right",
    rating: 79,
    heightCm: 187,
    ability: "Lane Guard",
    abilityDetail: "Reads driven passes through the central channel.",
    attributes: { pace: 68, shooting: 38, passing: 68, dribbling: 61, defending: 82, physical: 82 }
  },
  {
    id: "nala-prasetya",
    name: "Nala Prasetya",
    shortName: "Nala",
    number: 5,
    position: "CB",
    alternatePositions: [],
    preferredFoot: "Left",
    rating: 78,
    heightCm: 184,
    ability: "Clean Step",
    abilityDetail: "Improves recovery after a well-timed standing challenge.",
    attributes: { pace: 72, shooting: 42, passing: 70, dribbling: 64, defending: 80, physical: 78 }
  },
  {
    id: "damar-satya",
    name: "Damar Satya",
    shortName: "Damar",
    number: 6,
    position: "CM",
    alternatePositions: ["CB"],
    preferredFoot: "Right",
    rating: 77,
    heightCm: 181,
    ability: "Safe Outlet",
    abilityDetail: "Finds a short release option after regaining possession.",
    attributes: { pace: 70, shooting: 65, passing: 79, dribbling: 74, defending: 76, physical: 77 }
  },
  {
    id: "rafi-wicak",
    name: "Rafi Wicak",
    shortName: "Rafi",
    number: 8,
    position: "CM",
    alternatePositions: [],
    preferredFoot: "Right",
    rating: 79,
    heightCm: 176,
    ability: "Tempo Needle",
    abilityDetail: "Sharper first-time short passes under nearby pressure.",
    attributes: { pace: 75, shooting: 72, passing: 83, dribbling: 82, defending: 69, physical: 70 }
  },
  {
    id: "jaya-ramadhan",
    name: "Jaya Ramadhan",
    shortName: "Jaya",
    number: 10,
    position: "CM",
    alternatePositions: ["ST"],
    preferredFoot: "Left",
    rating: 80,
    heightCm: 178,
    ability: "Open Window",
    abilityDetail: "Recognizes forward passing lanes earlier around the box.",
    attributes: { pace: 77, shooting: 78, passing: 84, dribbling: 83, defending: 58, physical: 68 }
  },
  {
    id: "kian-mahesa",
    name: "Kian Mahesa",
    shortName: "Kian",
    number: 11,
    position: "LW",
    alternatePositions: ["RW"],
    preferredFoot: "Right",
    rating: 78,
    heightCm: 174,
    ability: "Inside Burst",
    abilityDetail: "Carries momentum when cutting from the flank toward goal.",
    attributes: { pace: 86, shooting: 75, passing: 74, dribbling: 84, defending: 38, physical: 62 }
  },
  {
    id: "miko-aditya",
    name: "Miko Aditya",
    shortName: "Miko",
    number: 9,
    position: "ST",
    alternatePositions: [],
    preferredFoot: "Right",
    rating: 81,
    heightCm: 183,
    ability: "Quiet Finish",
    abilityDetail: "Settles quicker before a balanced finish inside the area.",
    attributes: { pace: 82, shooting: 85, passing: 69, dribbling: 79, defending: 32, physical: 78 }
  },
  {
    id: "tara-banyu",
    name: "Tara Banyu",
    shortName: "Tara",
    number: 7,
    position: "RW",
    alternatePositions: ["LW"],
    preferredFoot: "Left",
    rating: 79,
    heightCm: 172,
    ability: "Curved Delivery",
    abilityDetail: "Adds controlled bend to crosses from a stable body position.",
    attributes: { pace: 85, shooting: 77, passing: 78, dribbling: 85, defending: 41, physical: 61 }
  },
  {
    id: "lazuardi-nur",
    name: "Lazuardi Nur",
    shortName: "Lazu",
    number: 12,
    position: "GK",
    alternatePositions: [],
    preferredFoot: "Left",
    rating: 74,
    heightCm: 191,
    ability: "Wide Claim",
    abilityDetail: "Commits more decisively to reachable high deliveries.",
    attributes: { pace: 45, shooting: 16, passing: 67, dribbling: 47, defending: 73, physical: 77 }
  },
  {
    id: "galih-perkasa",
    name: "Galih Perkasa",
    shortName: "Galih",
    number: 14,
    position: "CB",
    alternatePositions: ["RB"],
    preferredFoot: "Right",
    rating: 75,
    heightCm: 185,
    ability: "Aerial Frame",
    abilityDetail: "Improves positioning before contested high balls.",
    attributes: { pace: 70, shooting: 40, passing: 65, dribbling: 59, defending: 77, physical: 80 }
  },
  {
    id: "reza-purnama",
    name: "Reza Purnama",
    shortName: "Reza",
    number: 15,
    position: "LB",
    alternatePositions: ["RB"],
    preferredFoot: "Left",
    rating: 75,
    heightCm: 175,
    ability: "Recovery Arc",
    abilityDetail: "Turns more cleanly when tracking a runner toward his own goal.",
    attributes: { pace: 83, shooting: 51, passing: 70, dribbling: 76, defending: 71, physical: 66 }
  },
  {
    id: "bagas-laksana",
    name: "Bagas Laksana",
    shortName: "Bagas",
    number: 16,
    position: "CM",
    alternatePositions: [],
    preferredFoot: "Right",
    rating: 76,
    heightCm: 180,
    ability: "Second Wave",
    abilityDetail: "Arrives later into open midfield shooting space.",
    attributes: { pace: 74, shooting: 73, passing: 76, dribbling: 75, defending: 68, physical: 73 }
  },
  {
    id: "indra-kirana",
    name: "Indra Kirana",
    shortName: "Indra",
    number: 17,
    position: "LW",
    alternatePositions: ["ST"],
    preferredFoot: "Right",
    rating: 77,
    heightCm: 176,
    ability: "Direct Touch",
    abilityDetail: "Pushes the ball into useful space on a forward first touch.",
    attributes: { pace: 87, shooting: 76, passing: 70, dribbling: 81, defending: 35, physical: 65 }
  },
  {
    id: "fajar-nugraha",
    name: "Fajar Nugraha",
    shortName: "Fajar",
    number: 18,
    position: "ST",
    alternatePositions: ["RW"],
    preferredFoot: "Left",
    rating: 77,
    heightCm: 181,
    ability: "Near-Post Step",
    abilityDetail: "Creates a small separation window on short box runs.",
    attributes: { pace: 80, shooting: 80, passing: 67, dribbling: 76, defending: 31, physical: 74 }
  },
  {
    id: "bayu-samudra",
    name: "Bayu Samudra",
    shortName: "Bayu",
    number: 20,
    position: "RW",
    alternatePositions: ["CM"],
    preferredFoot: "Right",
    rating: 76,
    heightCm: 173,
    ability: "Quick Return",
    abilityDetail: "Accelerates into space after completing a short pass.",
    attributes: { pace: 84, shooting: 71, passing: 76, dribbling: 80, defending: 44, physical: 60 }
  }
];

export const FORMATIONS: Record<FormationId, FormationDefinition> = {
  "4-3-3": {
    id: "4-3-3",
    name: "4-3-3 Balanced",
    summary: "Wide front three with a connected midfield triangle.",
    slots: [
      { id: "gk", role: "GK", label: "GK", x: 50, y: 89, anchorX: -48, anchorZ: 0 },
      { id: "lb", role: "LB", label: "LB", x: 13, y: 70, anchorX: -31, anchorZ: -25 },
      { id: "lcb", role: "CB", label: "CB", x: 37, y: 74, anchorX: -35, anchorZ: -9 },
      { id: "rcb", role: "CB", label: "CB", x: 63, y: 74, anchorX: -35, anchorZ: 9 },
      { id: "rb", role: "RB", label: "RB", x: 87, y: 70, anchorX: -31, anchorZ: 25 },
      { id: "lcm", role: "CM", label: "CM", x: 27, y: 48, anchorX: -14, anchorZ: -13 },
      { id: "cm", role: "CM", label: "CM", x: 50, y: 53, anchorX: -10, anchorZ: 0 },
      { id: "rcm", role: "CM", label: "CM", x: 73, y: 48, anchorX: -14, anchorZ: 13 },
      { id: "lw", role: "LW", label: "LW", x: 18, y: 21, anchorX: 8, anchorZ: -23 },
      { id: "st", role: "ST", label: "ST", x: 50, y: 15, anchorX: 14, anchorZ: 0 },
      { id: "rw", role: "RW", label: "RW", x: 82, y: 21, anchorX: 8, anchorZ: 23 }
    ]
  },
  "4-4-2": {
    id: "4-4-2",
    name: "4-4-2 Compact",
    summary: "Two clear banks protect central space behind a strike pair.",
    slots: [
      { id: "gk", role: "GK", label: "GK", x: 50, y: 89, anchorX: -48, anchorZ: 0 },
      { id: "lb", role: "LB", label: "LB", x: 13, y: 70, anchorX: -31, anchorZ: -25 },
      { id: "lcb", role: "CB", label: "CB", x: 37, y: 74, anchorX: -35, anchorZ: -9 },
      { id: "rcb", role: "CB", label: "CB", x: 63, y: 74, anchorX: -35, anchorZ: 9 },
      { id: "rb", role: "RB", label: "RB", x: 87, y: 70, anchorX: -31, anchorZ: 25 },
      { id: "lm", role: "LW", label: "LM", x: 13, y: 45, anchorX: -9, anchorZ: -24 },
      { id: "lcm", role: "CM", label: "CM", x: 39, y: 48, anchorX: -12, anchorZ: -8 },
      { id: "rcm", role: "CM", label: "CM", x: 61, y: 48, anchorX: -12, anchorZ: 8 },
      { id: "rm", role: "RW", label: "RM", x: 87, y: 45, anchorX: -9, anchorZ: 24 },
      { id: "lst", role: "ST", label: "ST", x: 37, y: 18, anchorX: 12, anchorZ: -8 },
      { id: "rst", role: "ST", label: "ST", x: 63, y: 18, anchorX: 12, anchorZ: 8 }
    ]
  },
  "4-2-3-1": {
    id: "4-2-3-1",
    name: "4-2-3-1 Control",
    summary: "A double pivot supports three creators behind one striker.",
    slots: [
      { id: "gk", role: "GK", label: "GK", x: 50, y: 89, anchorX: -48, anchorZ: 0 },
      { id: "lb", role: "LB", label: "LB", x: 13, y: 70, anchorX: -31, anchorZ: -25 },
      { id: "lcb", role: "CB", label: "CB", x: 37, y: 74, anchorX: -35, anchorZ: -9 },
      { id: "rcb", role: "CB", label: "CB", x: 63, y: 74, anchorX: -35, anchorZ: 9 },
      { id: "rb", role: "RB", label: "RB", x: 87, y: 70, anchorX: -31, anchorZ: 25 },
      { id: "ldm", role: "CM", label: "DM", x: 37, y: 55, anchorX: -18, anchorZ: -10 },
      { id: "rdm", role: "CM", label: "DM", x: 63, y: 55, anchorX: -18, anchorZ: 10 },
      { id: "lw", role: "LW", label: "LW", x: 17, y: 34, anchorX: 1, anchorZ: -23 },
      { id: "cam", role: "CM", label: "AM", x: 50, y: 35, anchorX: 0, anchorZ: 0 },
      { id: "rw", role: "RW", label: "RW", x: 83, y: 34, anchorX: 1, anchorZ: 23 },
      { id: "st", role: "ST", label: "ST", x: 50, y: 14, anchorX: 15, anchorZ: 0 }
    ]
  }
};

export const TACTICAL_PRESETS: Record<TacticalPresetId, TacticalPreset> = {
  measured: {
    id: "measured",
    name: "Measured Shape",
    summary: "Balanced support, patient pressure, and stable spacing.",
    buildUp: 52,
    width: 50,
    pressure: 48
  },
  vertical: {
    id: "vertical",
    name: "Vertical Surge",
    summary: "Faster forward support with more aggressive recovery runs.",
    buildUp: 82,
    width: 58,
    pressure: 72
  },
  control: {
    id: "control",
    name: "Orbit Control",
    summary: "Shorter support distances and wider possession recycling.",
    buildUp: 38,
    width: 68,
    pressure: 42
  },
  shield: {
    id: "shield",
    name: "Compact Shield",
    summary: "Deeper support anchors with conservative pressing.",
    buildUp: 32,
    width: 38,
    pressure: 30
  }
};

const PLAYER_BY_ID = new Map(SQUAD_PLAYERS.map((player) => [player.id, player]));

function compatibility(player: SquadPlayer, role: PlayerRole): number {
  if (player.position === role) return 12;
  if (player.alternatePositions.includes(role)) return 7;
  if (player.position === "GK" || role === "GK") return -1000;
  const attackingRoles: PlayerRole[] = ["LW", "RW", "ST"];
  const defensiveRoles: PlayerRole[] = ["LB", "CB", "RB"];
  if (attackingRoles.includes(player.position) && attackingRoles.includes(role)) return -3;
  if (defensiveRoles.includes(player.position) && defensiveRoles.includes(role)) return -3;
  return -8;
}

export function autoBuildLineup(formationId: FormationId): string[] {
  const selected = new Set<string>();
  return FORMATIONS[formationId].slots.map((slot) => {
    const candidate = [...SQUAD_PLAYERS]
      .filter((player) => !selected.has(player.id))
      .sort(
        (a, b) =>
          b.rating + compatibility(b, slot.role) - (a.rating + compatibility(a, slot.role))
      )[0]!;
    selected.add(candidate.id);
    return candidate.id;
  });
}

export function createDefaultClubPlan(): ClubPlan {
  const startingIds = autoBuildLineup("4-3-3");
  return {
    schemaVersion: 1,
    formationId: "4-3-3",
    tacticalPreset: "measured",
    startingIds,
    captainId: "jaya-ramadhan",
    selectedPlayerId: "jaya-ramadhan"
  };
}

export function normalizeClubPlan(value: Partial<ClubPlan> | null | undefined): ClubPlan {
  const formationId = value?.formationId && FORMATIONS[value.formationId]
    ? value.formationId
    : "4-3-3";
  const tacticalPreset =
    value?.tacticalPreset && TACTICAL_PRESETS[value.tacticalPreset]
      ? value.tacticalPreset
      : "measured";
  const savedLineup = Array.isArray(value?.startingIds)
    ? value.startingIds.filter((id) => PLAYER_BY_ID.has(id))
    : [];
  const startingIds =
    savedLineup.length === 11 && new Set(savedLineup).size === 11
      ? savedLineup
      : autoBuildLineup(formationId);
  const captainId =
    value?.captainId && startingIds.includes(value.captainId)
      ? value.captainId
      : startingIds.find((id) => PLAYER_BY_ID.get(id)?.position !== "GK") ?? startingIds[0]!;
  const selectedPlayerId =
    value?.selectedPlayerId && PLAYER_BY_ID.has(value.selectedPlayerId)
      ? value.selectedPlayerId
      : captainId;

  return {
    schemaVersion: 1,
    formationId,
    tacticalPreset,
    startingIds,
    captainId,
    selectedPlayerId
  };
}

export function playerById(id: string): SquadPlayer | undefined {
  return PLAYER_BY_ID.get(id);
}

export function lineupPlayers(plan: ClubPlan): SquadPlayer[] {
  return plan.startingIds
    .map((id) => PLAYER_BY_ID.get(id))
    .filter((player): player is SquadPlayer => Boolean(player));
}

export function benchPlayers(plan: ClubPlan): SquadPlayer[] {
  const starting = new Set(plan.startingIds);
  return SQUAD_PLAYERS.filter((player) => !starting.has(player.id));
}

export function teamRating(plan: ClubPlan): number {
  const players = lineupPlayers(plan);
  if (!players.length) return 0;
  return Math.round(players.reduce((total, player) => total + player.rating, 0) / players.length);
}

export function compatibleSwapTargets(plan: ClubPlan, incomingId: string): SquadPlayer[] {
  const incoming = PLAYER_BY_ID.get(incomingId);
  if (!incoming) return [];
  return plan.startingIds
    .map((id, index) => ({ player: PLAYER_BY_ID.get(id), slot: FORMATIONS[plan.formationId].slots[index] }))
    .filter(
      (entry): entry is { player: SquadPlayer; slot: FormationSlot } =>
        Boolean(entry.player && entry.slot) && compatibility(incoming, entry.slot!.role) >= -3
    )
    .map((entry) => entry.player);
}

export function swapIntoLineup(
  plan: ClubPlan,
  incomingId: string,
  outgoingId: string
): ClubPlan {
  const incoming = PLAYER_BY_ID.get(incomingId);
  const outgoingIndex = plan.startingIds.indexOf(outgoingId);
  const slot = FORMATIONS[plan.formationId].slots[outgoingIndex];
  if (!incoming || outgoingIndex < 0 || !slot || compatibility(incoming, slot.role) < -3) {
    return plan;
  }
  const startingIds = [...plan.startingIds];
  startingIds[outgoingIndex] = incomingId;
  return {
    ...plan,
    startingIds,
    captainId: plan.captainId === outgoingId ? incomingId : plan.captainId,
    selectedPlayerId: incomingId
  };
}
